import { ApolloError } from "@apollo/client";
import { Classes } from "@blueprintjs/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { HexMenuItem, Txt } from "../../../hex-components";
import {
  HexCollapse,
  ToggleHeader,
} from "../../../hex-components/HexCollapse.js";
import {
  LocalStorageKeys,
  useLocalStorage,
} from "../../../hooks/useLocalStorage.js";
import { useToggleState } from "../../../hooks/useToggleState";
import { useHexFlag } from "../../../util/useHexFlags.js";
import { MoreMenuIcon } from "../../icons/CustomIcons";
import { SIDEBAR_PADDING } from "../../sidebar/sidebarSharedStyles.js";

const StyledCollapse = styled(HexCollapse)`
  width: 100%;

  li {
    list-style-type: none;
  }

  & ${ToggleHeader} {
    justify-content: flex-start;
    gap: 4px;
  }

  && {
    margin: 0;
  }

  > :first-child {
    padding-top: ${SIDEBAR_PADDING / 2}px;
  }
`;

const Header = styled.div`
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.fontSize.HEADING_FIVE};
  color: ${({ theme }) => theme.fontColor.MUTED};
  padding-left: 4px;
`;

const EmptyFavorite = styled.div`
  color: ${({ theme }) => theme.fontColor.PLACEHOLDER};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  padding: 7px 4px;
  padding-left: 12px;
  min-height: 30px;
`;

const SkeletonRow = styled.div`
  height: 16px;
  padding: 2px 8px;
  display: flex;
  align-items: center;
  &:last-child {
    margin-bottom: 10px;
  }
`;

const SkeletonBar = styled.div`
  height: 12px;
  border-radius: 3px;
`;

const SKELETON_WIDTHS = ["30%", "80%", "50%", "40%", "40%"];

const SkeletonRows = React.memo(function SkeletonRows() {
  return (
    <>
      {SKELETON_WIDTHS.map((width, i) => (
        <SkeletonRow key={i}>
          <SkeletonBar className={Classes.SKELETON} style={{ width }} />
        </SkeletonRow>
      ))}
    </>
  );
});

// ---------------------------------------------------------------------------
// Coordinated loading context: all sidebar sections wait for each other so
// they appear simultaneously instead of popping in one at a time.
// ---------------------------------------------------------------------------
interface SidebarSectionsLoadingContextType {
  anyLoading: boolean;
  reportLoading: (key: string, isLoading: boolean) => void;
}

const SidebarSectionsLoadingContext =
  React.createContext<SidebarSectionsLoadingContextType | null>(null);

export const SidebarSectionsLoadingProvider: React.ComponentType<{
  children: React.ReactNode;
}> = React.memo(function SidebarSectionsLoadingProvider({ children }) {
  const navSidebarCleanup = useHexFlag("nav-sidebar-cleanup");
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});

  const reportLoading = useCallback((key: string, isLoading: boolean) => {
    setLoadingMap((prev) => {
      if (prev[key] === isLoading) return prev;
      return { ...prev, [key]: isLoading };
    });
  }, []);

  const registeredKeys = Object.keys(loadingMap);
  const anyLoading =
    registeredKeys.length === 0 || Object.values(loadingMap).some(Boolean);

  const value = useMemo(
    () => ({ anyLoading, reportLoading }),
    [anyLoading, reportLoading],
  );

  return (
    <SidebarSectionsLoadingContext.Provider value={value}>
      {navSidebarCleanup && anyLoading && <SkeletonRows />}
      {children}
    </SidebarSectionsLoadingContext.Provider>
  );
});

// ---------------------------------------------------------------------------

type SidebarCollapsedStateFromStorage = Pick<
  typeof LocalStorageKeys,
  | "SIDEBAR_FAVORITE_COLLECTIONS_OPENED"
  | "SIDEBAR_FAVORITE_PROJECTS_OPENED"
  | "SIDEBAR_FAVORITE_DATA_OPENED"
  | "SIDEBAR_RECENT_ITEMS_OPENED"
>;
const SHOW_MORE_WHEN_MORE_THAN = 8;
const SHOW_MORE_NUM_ITEMS_COLLAPSED = 5;

interface SidebarSectionProps {
  items: React.ReactNode[];
  loading: boolean;
  error: ApolloError | undefined;
  text: string;
  /**
   * Boolean LocalStorage key used to indicate whether or not the sidebar section
   * should be opened or closed.
   */
  localStorageKey: keyof SidebarCollapsedStateFromStorage;
  hideIfEmpty?: boolean;
}
export const SidebarSection: React.FunctionComponent<SidebarSectionProps> = ({
  error,
  hideIfEmpty = false,
  items,
  loading,
  localStorageKey,
  text,
}) => {
  const navSidebarCleanup = useHexFlag("nav-sidebar-cleanup");
  const loadingContext = React.useContext(SidebarSectionsLoadingContext);
  const reportLoading = loadingContext?.reportLoading;

  useEffect(() => {
    reportLoading?.(localStorageKey, loading);
  }, [reportLoading, localStorageKey, loading]);

  const [isOpened, setIsOpened] = useLocalStorage(
    LocalStorageKeys[localStorageKey],
  );
  const [showMore, _, { toggle: toggleShowMore }] = useToggleState(false);

  const toggleCallback = useCallback(() => {
    setIsOpened(!isOpened);
  }, [setIsOpened, isOpened]);

  const shouldShowMore = items.length > SHOW_MORE_WHEN_MORE_THAN;

  const displayedItems = useMemo(() => {
    if (showMore) {
      return items;
    }
    return items.slice(0, SHOW_MORE_NUM_ITEMS_COLLAPSED);
  }, [items, showMore]);

  if (error) {
    return null;
  }

  // If a coordinated loading provider exists, defer to it: render nothing
  // while any sibling section is still loading (the provider shows a single
  // unified skeleton). Without the provider, fall back to per-section skeleton
  // (only when the flag is on; the old behavior just hides the section).
  if (loadingContext) {
    if (loadingContext.anyLoading) {
      return null;
    }
  } else if (loading) {
    return navSidebarCleanup ? <SkeletonRows /> : null;
  }

  if (hideIfEmpty && items.length === 0) {
    return null;
  }

  return (
    <StyledCollapse
      isOpen={isOpened}
      small={true}
      title={<Header>{text}</Header>}
      toggleIsOpen={toggleCallback}
      togglePosition="right"
    >
      {items.length === 0 ? (
        <EmptyFavorite>{text} will appear here.</EmptyFavorite>
      ) : (
        <>
          {displayedItems}
          {shouldShowMore && (
            <HexMenuItem
              icon={<MoreMenuIcon />}
              text={<Txt fontColor="muted">{showMore ? "Less" : "More"}</Txt>}
              onClick={toggleShowMore}
            />
          )}
        </>
      )}
    </StyledCollapse>
  );
};
